import React from 'react';
import TaskBar from '../../components/taskbar/TaskBar';
import Footer from '../../components/footer/Footer';
import styles from './CancelPolicyPage.module.css';

export default function CancelPolicyPage() {
  return (
    <>
      <TaskBar />
      <div className={styles.cancelPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>Cancellation Policy</h1>
          <p className={styles.heroSubtitle}>
            Last updated: November 26, 2025
          </p>
        </section>

        {/* Cancellation Policy Content */}
        <section className={styles.content}>
          <div className={styles.contentWrapper}>
            
            {/* Overview */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <p className={styles.paragraph}>
                At SiameseAI, we want you to be completely satisfied with our service. 
                You can cancel your subscription at any time with no questions asked.
              </p>
            </div>

            {/* How to Cancel */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>How to Cancel Your Subscription</h2>
              <ul className={styles.list}>
                <li>
                  <strong>Log in to your account</strong> at <a href="/login" className={styles.link}>foundry.siameseai.com/login</a>
                </li>
                <li>
                  <strong>Navigate to Billing Settings</strong> by clicking on your profile 
                  icon and selecting "Settings" → "Billing"
                </li>
                <li>
                  <strong>Click "Cancel Subscription"</strong> and confirm your cancellation
                </li>
              </ul>
              <p className={styles.paragraph}>
                Alternatively, you can manage your subscription directly through the{' '}
                <a 
                  href="https://billing.stripe.com/p/login/test_00g15naBs5om08g000" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  Stripe Customer Portal
                </a>.
              </p>
            </div>

            {/* Free Trial Period */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Free Trial Period</h2>
              <h3 className={styles.subsectionTitle}>7-Day Free Trial</h3>
              <p className={styles.paragraph}>
                When you sign up for SiameseAI, you receive a 7-day free trial with $25 in credits to explore our platform.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>During the trial:</strong> You won't be charged. Feel free to use 
                  all features within your credit limit.
                </li>
                <li>
                  <strong>Cancel anytime:</strong> If you cancel before the trial ends, you 
                  won't be charged at all.
                </li>
                <li>
                  <strong>Trial reminder:</strong> We'll send you a reminder email 3 days 
                  before your trial ends.
                </li>
                <li>
                  <strong>After trial:</strong> If you don't cancel, you'll automatically be 
                  charged $50/month for the Pro plan.
                </li>
              </ul>
            </div>

            {/* What Happens After Cancellation */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>What Happens After Cancellation</h2>
              <ul className={styles.list}>
                <li>
                  <strong>Access retention:</strong> You'll retain full access to your account 
                  until the end of your current billing period.
                </li>
                <li>
                  <strong>No prorated refunds:</strong> We don't offer refunds for partial months. 
                  You'll have access for the time you've paid for.
                </li>
                <li>
                  <strong>Data retention:</strong> Your projects and data will be preserved for 
                  30 days after cancellation in case you change your mind.
                </li>
                <li>
                  <strong>Re-activation:</strong> You can reactivate your subscription at any 
                  time by logging back in and subscribing again.
                </li>
              </ul>
            </div>

            {/* Billing and Charges */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Billing and Charges</h2>
              
              <h3 className={styles.subsectionTitle}>Subscription Plans</h3>
              <ul className={styles.list}>
                <li><strong>Pro Plan:</strong> $50/month (after 7-day trial)</li>
                <li><strong>Business Plan:</strong> $200/month</li>
                <li><strong>Enterprise Plan:</strong> Custom pricing - contact us</li>
              </ul>

              <h3 className={styles.subsectionTitle}>Usage-Based Charges</h3>
              <p className={styles.paragraph}>
                In addition to your monthly subscription, you may incur usage-based charges for:
              </p>
              <ul className={styles.list}>
                <li>AI API tokens (OpenAI, Anthropic)</li>
                <li>Cloud Server hours</li>
                <li>Additional storage beyond plan limits</li>
              </ul>
              <p className={styles.paragraph}>
                These charges are based on your actual usage and are billed at the end of each 
                billing cycle. Cancelling your subscription will stop all usage-based charges 
                immediately.
              </p>
            </div>

            {/* Refund Policy */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Refund Policy</h2>
              <p className={styles.paragraph}>
                While we don't offer refunds for subscription fees, we want you to be happy 
                with our service. If you experience any issues:
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Technical problems:</strong> Contact our support team and we'll work 
                  to resolve them.
                </li>
                <li>
                  <strong>Billing errors:</strong> If you were charged incorrectly, contact us 
                  immediately and we'll fix it.
                </li>
                <li>
                  <strong>Unauthorized charges:</strong> Report any unauthorized charges within 
                  30 days for investigation.
                </li>
              </ul>
            </div>

            {/* Pausing Your Subscription */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Pausing Your Subscription</h2>
              <p className={styles.paragraph}>
                Currently, we don't offer a subscription pause feature. If you need to take a 
                break, we recommend:
              </p>
              <ul className={styles.list}>
                <li>Exporting your important data</li>
                <li>Cancelling your subscription</li>
                <li>Re-subscribing when you're ready to return</li>
              </ul>
              <p className={styles.paragraph}>
                Your data will be retained for 30 days after cancellation, giving you time to 
                come back without losing your work.
              </p>
            </div>

            {/* Contact Us */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Contact Us</h2>
              <p className={styles.paragraph}>
                If you have any questions about cancellation or need help with the process, 
                please contact us:
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@siameseai.com" className={styles.link}>
                    support@siameseai.com
                  </a>
                </li>
              </ul>
            </div>

            {/* Changes to This Policy */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Changes to This Policy</h2>
              <p className={styles.paragraph}>
                We may update this cancellation policy from time to time. We'll notify you of 
                any significant changes via email or through a notice on our website.
              </p>
              <p className={styles.paragraph}>
                For more information, please see our{' '}
                <a href="/terms" className={styles.link}>Terms of Service</a> and{' '}
                <a href="/privacy" className={styles.link}>Privacy Policy</a>.
              </p>
            </div>

            {/* Contact Information */}
            <div className={styles.section}>
              <div className={styles.contactInfo}>
                <p><strong>SiameseAI Inc.</strong></p>
                <p>PO Box 23446</p>
                <p>San Jose, CA 95153</p>
                <p>Email: <a href="mailto:support@siameseai.com" className={styles.link}>support@siameseai.com</a></p>
              </div>
            </div>

          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
